import React, { useEffect, useState, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Dropdown from 'components/Dropdown/Dropdown';
import { Icon } from 'components/ui';
import {
    colors, space, fontWeights, lineHeights
} from 'style/config';
import { getContextAwarePlacement } from 'ai/utils/aiBeautyChat';
import AIBeautyChatContent from 'ai/components/AIBeautyChatDrop/AIBeautyChatContent.f';
import { trackFlyoutOpen } from 'ai/analytics';

const AIBeautyChatDrop = props => {
    const [isOpen, setIsOpen] = useState(false);
    const {
        dropWidth,
        config,
        localization,
        page,
        featuresAIBeautyChat,
        contextPrompts,
        handlePromptClick,
        clearContextPrompts,
        fetchEntryPoints,
        clientId,
        anonymousId,
        session
    } = props;

    const lastFetchedContextRef = useRef(null);
    const isFetchingRef = useRef(false);

    useEffect(() => {
        const placement = getContextAwarePlacement(page);
        const isNonContextAwarePage = !placement;
        const isSRP = placement === 'SRP';
        const currentContext = page?.templateInformation?.template || 'home';

        if (isNonContextAwarePage || isSRP) {
            const hasNoPrompts = contextPrompts.length === 0;
            const contextChanged = lastFetchedContextRef.current !== null && lastFetchedContextRef.current !== currentContext;
            const shouldFetch = (hasNoPrompts || contextChanged) && !isFetchingRef.current;

            if (shouldFetch) {
                isFetchingRef.current = true;
                clearContextPrompts();
                fetchEntryPoints(clientId, anonymousId, page)
                    .then(() => {
                        lastFetchedContextRef.current = currentContext;
                    })
                    .catch(() => {
                        lastFetchedContextRef.current = null;
                    })
                    .finally(() => {
                        isFetchingRef.current = false;
                    });
            }
        } else {
            lastFetchedContextRef.current = currentContext;
        }
    }, [
        page?.templateInformation?.template,
        page?.nthCategory?.categoryId,
        page?.product?.productId,
        page?.search?.keyword,
        contextPrompts.length,
        clientId,
        anonymousId,
        clearContextPrompts,
        fetchEntryPoints
    ]);

    const hasTrackedFlyoutRef = useRef(false);
    useEffect(() => {
        if (isOpen && !hasTrackedFlyoutRef.current) {
            hasTrackedFlyoutRef.current = true;
            trackFlyoutOpen();
        } else if (!isOpen) {
            hasTrackedFlyoutRef.current = false;
        }
    }, [isOpen]);

    const contentProps = {
        localization,
        page,
        featuresAIBeautyChat,
        contextPrompts,
        handlePromptClick,
        session
    };

    return (
        <Dropdown
            id='aibeautychat_drop'
            hasMaxHeight={true}
            onTrigger={(_, isDropdownOpen) => {
                setIsOpen(isDropdownOpen);
            }}
        >
            <Dropdown.Trigger
                display='flex'
                alignItems='center'
                paddingX={4}
                height='100%'
                data-at={Sephora.debug.dataAt('aibeautychat_header')}
            >
                <Icon
                    name={isOpen ? 'aiBeautyActive' : 'aiBeauty'}
                    size={24}
                    marginRight={3}
                />
                <span css={styles.labelContainer}>
                    <span css={isOpen && styles.triggerLabelActive}>
                        {config?.meta?.bot_name || localization.title}
                        <span css={styles.badge}>{localization.new}</span>
                    </span>
                </span>
            </Dropdown.Trigger>
            <Dropdown.Menu
                width={dropWidth}
                minWidth='374px'
                align='center'
                paddingTop={4}
                paddingBottom={2}
                data-at={Sephora.debug.dataAt('aibeautychat_flyout_menu')}
            >
                <AIBeautyChatContent {...contentProps} />
            </Dropdown.Menu>
        </Dropdown>
    );
};

const styles = {
    triggerLabel: {
        marginLeft: space[3]
    },
    triggerLabelActive: {
        boxShadow: `0 2px 0 0 ${colors.black}`
    },
    labelContainer: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: 65,
        lineHeight: lineHeights.tight
    },
    badge: {
        padding: '1px 2px',
        color: colors.white,
        backgroundColor: colors.black,
        borderRadius: '2px',
        fontSize: '7px',
        fontWeight: fontWeights.bold,
        lineHeight: 1,
        textTransform: 'uppercase',
        top: -2,
        left: 4,
        position: 'relative',
        height: '10px',
        display: 'inline-flex',
        alignItems: 'center'
    }
};

export default wrapFunctionalComponent(AIBeautyChatDrop, 'AIBeautyChatDrop');
